import React, { useState } from 'react';
import { useTranslation } from '../../../i18n';
import { Question } from '../../../types/game';
import { QuestionHint } from '../QuestionHint/QuestionHint';
import { PairMatching } from '../PairMatching/PairMatching';
import WaitingSpinner from '../WaitingSpinner';
import { pickOptionAnimationForIndex, pickOptionDelayForIndex } from '../../../constants/drunkVisionAnimations';
import './AnswerOptions.css';

interface AnswerOptionsProps {
  question: Question;
  selectedAnswer: string | null;
  answerSubmitted: boolean;
  hostIsPlaying: boolean;
  showHint: boolean;
  gameType: string;
  onAnswerSelect: (answer: string) => void;
  onSubmitAnswer: (e?: React.MouseEvent) => void;
  answerCorrect: boolean | null;
  waitingForOthers: boolean;
  isSubmitting: boolean;
  selectedPairs?: Record<string, string>;
  onPairSelect?: (leftItem: string, rightItem: string) => void;
}

/**
 * Reusable component for rendering interactive answer options in host displays.
 * Supports host playing mode with selection, submission, and feedback.
 */
export const AnswerOptions: React.FC<AnswerOptionsProps> = ({
  question,
  selectedAnswer,
  answerSubmitted,
  hostIsPlaying,
  showHint,
  gameType,
  onAnswerSelect,
  onSubmitAnswer,
  answerCorrect,
  waitingForOthers,
  isSubmitting,
  selectedPairs = {},
  onPairSelect,
}) => {
  const { t } = useTranslation();
  const isClickable = hostIsPlaying && !answerSubmitted;

  // Pair matching games should not show answer options on host screen
  // The interface is only shown on player screens
  const isPairMatching = gameType === 'pair-matching' || gameType === 'team-pair-matching' || gameType === 'party-pair-matching' || gameType === 'family-pair-matching';
  if (isPairMatching) {
    // Don't render anything for pair matching on host screen
    return null;
  }

  return (
    <div className="answer-options">
      {/* Hint for collaborative games - shown after 5 seconds */}
      {showHint && question.hint && (gameType === 'collaborative-puzzle' || gameType === 'story-chain') && (
        <QuestionHint hint={question.hint} variant="answers" />
      )}
      
      <div className="options-list">
        {question.options.map((option, index) => {
          const letter = String.fromCharCode(65 + index);
          const isSelected = selectedAnswer === option;
          const isAreYouDrunk = gameType === 'are-you-drunk' || gameType === 'vision-check';
          const questionId = question.questionId ?? question.id;
          const drunkOptionClass = isAreYouDrunk ? pickOptionAnimationForIndex(questionId, index) : '';
          const drunkOptionDelay = isAreYouDrunk ? pickOptionDelayForIndex(questionId, index) : 0;

          return (
            <div 
              key={index} 
              className={`option-item game-option-text ${question.isPicturePerfect ? 'picture-perfect' : ''} ${isSelected ? 'option-item--selected' : ''} ${answerSubmitted && hostIsPlaying ? 'option-item--submitted' : ''} ${drunkOptionClass}`}
              onClick={isClickable ? () => onAnswerSelect(option) : undefined}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                fontSize: 'inherit',
                backgroundColor: isSelected 
                  ? 'rgba(var(--color-primary-rgb), 0.3)' 
                  : answerSubmitted && hostIsPlaying 
                    ? 'rgba(248, 250, 252, 0.1)'
                    : 'rgba(248, 250, 252, 0.2)',
                border: isSelected ? '3px solid var(--color-primary)' : '2px solid transparent',
                opacity: answerSubmitted && hostIsPlaying ? 0.6 : 1,
                transition: 'all 0.2s ease',
                borderRadius: '8px',
                pointerEvents: !hostIsPlaying ? 'none' : 'auto',
                ...(isAreYouDrunk && drunkOptionClass ? { animationDelay: `${drunkOptionDelay}s` } : {}),
              }}
              onMouseEnter={(e) => {
                if (isClickable && !isSelected && hostIsPlaying) {
                  e.currentTarget.style.backgroundColor = 'rgba(248, 250, 252, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (hostIsPlaying && !isSelected) {
                  e.currentTarget.style.backgroundColor = answerSubmitted
                    ? 'rgba(248, 250, 252, 0.1)'
                    : 'rgba(248, 250, 252, 0.2)';
                }
              }}
            >
              {letter}. {option}
            </div>
          );
        })}
      </div>
      
      {/* Submit button when host is playing */}
      {hostIsPlaying && !answerSubmitted && (
        <button
          type="button"
          className="button answer-options__submit-button"
          onClick={onSubmitAnswer}
          disabled={!selectedAnswer || !question || answerSubmitted || isSubmitting}
        >
          {t('gameplay.submitAnswer')}
        </button>
      )}
      
      {/* Waiting message when host has submitted – same wrapper/loader as player screens */}
      {hostIsPlaying && answerSubmitted && waitingForOthers && (
        <div className="answer-options__waiting">
          {answerCorrect !== null && (
            <p className="answer-options__waiting-text" style={{ marginBottom: '0.5rem' }}>
              <span className={`answer-options__feedback answer-options__feedback--${answerCorrect ? 'correct' : 'wrong'}`}>
                {answerCorrect ? '✓ Correct!' : '✗ Wrong'}
              </span>
            </p>
          )}
          <WaitingSpinner isLoading={false} />
        </div>
      )}
    </div>
  );
};
